import React, { useState } from "react";
import { Button } from "react-bootstrap";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faX } from "@fortawesome/free-solid-svg-icons";
import HelpButton from "../../HelpContent/HelpButton";
import FullScreenLoadingIndicator from "../FullScreenLoadingIndicator";
import Input from "../../Builder/nodes/Logic/Inputs/Input";

export interface ApiProfileType {
  Uuid: string;
  Label: string;
  ClientId: number;
  BaseUrl: string;
  Authenticate: "Basic" | "Bearer";
  AuthHeader: string;
  AuthValue: string;
  Timeout: number;
  Category: string;
  Industry: string;
  DefaultMethod: "GET" | "POST" | "PUT" | "DELETE";
  QueryParameters: {
    Key: string;
    Value: string;
  }[];
  PostFormParameters: {
    Key: string;
    Value: string;
  }[];
  PostJSON: string;
  JsonPayload: any;
}

const el: any = document.querySelector("#app");
export const { contactsClientId, apiBase } = JSON.parse(
  el?.dataset?.page ?? ""
).props;

const headersList = {};

export async function proxyApiTry({
  prefix,
  url,
  method = "GET",
  body,
  formData,
  request,
  setLoading,
  headers = {},
}: {
  prefix: "logic/workflows/api" | "contacts/v1" | "logic/reporting";
  url: string;
  method?: "GET" | "POST" | "DELETE" | "PATCH";
  body?: any;
  formData?: any;
  request?: any;
  setLoading?: (isLoading: boolean) => void;
  headers?: Record<string, string>;
}) {
  let URL = url;
  if (URL[0] === "/") {
    URL = URL.slice(1);
  }
  try {
    setLoading?.(true);
    const response = await fetch(`${apiBase}/${prefix}/${URL}`, {
      method,
      headers: {
        ...headersList,
        ...headers,
        Authorization: `Bearer ${(window as any).apiToken}`,
      },
      // include body for post and delete
      ...(["POST", "DELETE", "PATCH"].includes(method) && body
        ? { body: JSON.stringify(body) }
        : {}),
      ...(formData ? { body: formData } : {}),

      // include request
      ...(request && { signal: request.signal }),
    });
    const data = await response.json();
    if (!response.ok) {
      if (response.status === 403) {
        toast.error(
          "You don't have enough permissions to perform this action."
        );
        setLoading?.(false);
        return null;
      }
      if (response.status === 401) {
        // token not valid - get new token
        if (data.token_expired) {
          const newTokenResponse = await fetch(`/api-gateway/get-new-token`, {
            headers: { Accept: "application/json" },
          });
          // php session expired - login
          if (newTokenResponse.status === 401) {
            toast.error("Session expired. Please login again.", {
              toastId: "session-expired",
            });
            setTimeout(() => {
              window.location.replace("/login");
              return;
            }, 1000);
          }
          // try again with new token
          const newTokenData = await newTokenResponse.json();
          const res = await fetch(`${apiBase}/${prefix}/${URL}`, {
            method,
            headers: {
              ...headersList,
              ...headers,
              Authorization: `Bearer ${newTokenData.token}`,
            },
            ...(method === "POST" ? { body: JSON.stringify(body) } : {}),
          });
          if (res.ok) {
            (window as any).apiToken = newTokenData.token;
          }
          setLoading?.(false);
          return await res.json();
        }
      }
      setLoading?.(false);
      if (response.status === 400) {
        toast.warning(data.error || data.message || "Bad Request.", {
          toastId: "error-message",
        });
        return data;
      }
      if (response.status === 500) {
        toast.error(data.error || data.message || "Something went wrong.", {
          toastId: "error-message",
        });
      }
      return null;
    }
    setLoading?.(false);
    return data;
  } catch (error) {
    console.log(error);
    toast.error("Something went wrong.\n" + error, {
      toastId: "error-message",
    });
    setLoading?.(false);
    return null;
  }
}

export default function ApiConnection({
  api: apiProfile,
  onChange: setApiProfile,
}: {
  api: any;
  onChange: (value: any) => void;
}) {
  const [testJSON, setTestJSON] = useState<{
    code?: number;
    data: string;
    message?: string;
  }>();
  const [loading, setLoading] = useState(false);

  const getExampleJSON = async () => {
    const data = await proxyApiTry({
      prefix: "logic/workflows/api",
      url: "api-try",
      method: "POST",
      body: { API: apiProfile },
      setLoading,
    });
    if (data) {
      setTestJSON(data);
    }
  };

  let username = "";
  let password = "";
  try {
    username =
      atob(apiProfile.AuthValue.replace("Basic", "").trim())?.split(":")[0] ||
      "";
  } catch {}

  try {
    password =
      atob(apiProfile.AuthValue.replace("Basic", "").trim())?.split(":")[1] ||
      "";
  } catch {}

  return (
    <div className="my-4">
      <FullScreenLoadingIndicator loading={loading} />
      <div>
        <h5>
          API Profile Options
          <HelpButton helpKey="api-profile-options" />
        </h5>
        <div className="mb-3">
          <label className="form-label">Base URL</label>
          <input
            type="text"
            className="form-control"
            value={apiProfile.BaseUrl}
            onChange={(e) =>
              setApiProfile({
                ...apiProfile,
                BaseUrl: e.target.value,
              })
            }
          />
        </div>
        <div className="mb-3">
          <label className="form-label">Auth Header</label>
          <HelpButton helpKey="api-profile-options-auth-header" />
          <div className="d-flex align-items-end mb-2 gap-2">
            <div style={{ minWidth: 150 }}>
              <label className="w-100">Type</label>
              <Input
                inputType="select"
                value={[apiProfile.AuthKind || ""]}
                onChange={([v]: any) => {
                  setApiProfile({
                    ...apiProfile,
                    AuthKind: v,
                  });
                }}
                options={[
                  { label: "Basic", value: "" },
                  { label: "Custom", value: "Custom" },
                ]}
              />
            </div>
            {apiProfile.AuthKind === "Custom" && (
              <label className="w-100">
                Custom Auth Header
                <input
                  type="text"
                  className="form-control"
                  style={{ height: 42 }}
                  value={apiProfile.AuthHeader}
                  onChange={(e) =>
                    setApiProfile({
                      ...apiProfile,
                      AuthHeader: e.target.value,
                    })
                  }
                />
              </label>
            )}
          </div>
        </div>
        {apiProfile.AuthKind === "" ? (
          <div className="d-flex mb-2 gap-2">
            <label className="w-100">
              Username
              <input
                type="text"
                className="form-control"
                style={{ height: 42 }}
                value={username}
                onChange={(e) =>
                  setApiProfile({
                    ...apiProfile,
                    AuthValue: "Basic " + btoa(e.target.value + ":" + password),
                  })
                }
              />
            </label>
            <label className="w-100">
              Password
              <input
                type="text"
                className="form-control"
                style={{ height: 42 }}
                value={password}
                onChange={(e) =>
                  setApiProfile({
                    ...apiProfile,
                    AuthValue: "Basic " + btoa(username + ":" + e.target.value),
                  })
                }
              />
            </label>
          </div>
        ) : (
          <div className="mb-3">
            <label className="form-label">Auth Value</label>
            <input
              type="text"
              className="form-control"
              value={apiProfile.AuthValue}
              onChange={(e) =>
                setApiProfile({
                  ...apiProfile,
                  AuthValue: e.target.value,
                })
              }
            />
          </div>
        )}
        <div className="mb-3">
          <label className="form-label">
            Timeout
            <HelpButton helpKey="api-profile-options-timeout" />
          </label>
          <input
            type="number"
            className="form-control"
            value={apiProfile.Timeout}
            onChange={(e) =>
              setApiProfile({
                ...apiProfile,
                Timeout: parseInt(e.target.value),
              })
            }
          />
        </div>
        <div className="mb-3"></div>

        <div className="mb-3">
          <label className="form-label">
            Default Method
            <HelpButton helpKey="api-profile-options-default-method" />
          </label>
          <select
            className="form-select"
            value={apiProfile.DefaultMethod}
            onChange={(e) =>
              setApiProfile({
                ...apiProfile,
                DefaultMethod: e.target.value as
                  | "GET"
                  | "POST"
                  | "PUT"
                  | "PATCH"
                  | "DELETE",
              })
            }
          >
            <option value="GET">GET</option>
            <option value="POST">POST</option>
            <option value="PUT">PUT</option>
            <option value="PATCH">PATCH</option>
            <option value="DELETE">DELETE</option>
          </select>
        </div>
      </div>

      <div className="d-flex justify-content-end my-5 gap-1 me-3">
        <Button
          className="rounded-pill px-3"
          variant="colored"
          onClick={getExampleJSON}
        >
          Test
        </Button>
      </div>
      {testJSON && (
        <div className="mb-3">
          <button
            className="btn float-end"
            onClick={() => setTestJSON(undefined)}
          >
            <FontAwesomeIcon icon={faX} />
          </button>
          <h5>API Response</h5>
          <div className="mb-2">
            Response code:{" "}
            <span
              className={`badge ${
                !!testJSON.code && testJSON.code >= 200 && testJSON.code < 300
                  ? "bg-success"
                  : "bg-danger"
              }`}
            >
              {testJSON.code}
            </span>
          </div>
          <div className="d-flex gap-2 mb-2">
            <div className="text-nowrap">Response message: </div>
            <div>{testJSON.message}</div>
          </div>
          <div className="mb-2">Response body:</div>
          <textarea
            className="form-control"
            style={{ height: 300 }}
            value={testJSON.data}
            readOnly
          />
        </div>
      )}
    </div>
  );
}
